import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Send,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Car,
  Navigation
} from "lucide-react";
import SEOHead from "../components/SEOHead";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Umoona Opal Mine & Museum",
    "description": "Contact Umoona Opal Mine & Museum in Coober Pedy. Get directions, hours, phone number and email. Book underground mine tours or inquire about authentic opals.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "14 Hutchison St",
      "addressLocality": "Coober Pedy",
      "addressRegion": "SA", 
      "postalCode": "5723",
      "addressCountry": "AU"
    },
    "telephone": "+61886725288",
    "openingHours": "Mo-Su 08:30-17:30",
    "url": "https://umoonaopalmine.com.au/contact"
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      // Here you would typically send the contact form to your backend
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      setError("Failed to send message. Please try calling us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEOHead
        title="Contact Us - Visit Umoona Opal Mine & Museum"
        description="Contact Umoona Opal Mine & Museum in Coober Pedy. Get directions, hours, phone number and email. Book underground mine tours or inquire about authentic opals."
        keywords="contact Umoona, Coober Pedy contact, opal mine contact, underground tours booking, museum contact"
        canonical="/contact"
        schemaData={schemaData}
      />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-900 via-teal-800 to-green-700">
        <div className="absolute inset-0">
          <img 
            src="/assets/oed03vp9_Turbo360-1043175.jpg"
            alt="Contact Umoona Opal Mine & Museum"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <Badge variant="secondary" className="mb-6 bg-teal-500/20 text-teal-300 border border-teal-400/30 px-6 py-3 text-lg">
            📞 Get in Touch • We're Here to Help
          </Badge>
          
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
            Contact
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-green-400">
              Umoona
            </span>
          </h1>
          
          <p className="text-xl lg:text-2xl text-teal-100 mb-8 max-w-4xl mx-auto">
            Planning your visit to Coober Pedy? Have questions about our tours, accommodation, or opals? 
            We're here to help make your underground experience unforgettable.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 text-center">
              <Phone className="w-8 h-8 text-teal-400 mx-auto mb-2" />
              <div className="text-xl font-bold text-white">(08) 8672 5288</div>
              <div className="text-teal-200 text-sm">Call Us Now</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 text-center">
              <Clock className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <div className="text-xl font-bold text-white">8:30am - 5:30pm</div>
              <div className="text-green-200 text-sm">Daily</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20 text-center">
              <CheckCircle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <div className="text-xl font-bold text-white">FREE</div>
              <div className="text-yellow-200 text-sm">Museum Entry</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="mining-cta font-bold px-8 py-4 text-lg">
              <a href="tel:(08) 8672 5288">
                Call Now
                <Phone className="ml-2 w-5 h-5" />
              </a>
            </Button>
            <Button 
              asChild
              variant="outline" 
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-8 py-4 text-lg backdrop-blur-sm"
            >
              <a href="mailto:admin@umoonaopalmine.com.au">
                Send Email
                <Mail className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Get In Touch
            </h2>
            <p className="text-xl text-gray-600">
              Ready to explore underground? Contact us for bookings, inquiries, or just to chat about opals!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-6 text-center">
              <CardContent className="space-y-4 p-0">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Phone className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold">Phone</h3>
                <p className="text-2xl font-bold text-blue-600">(08) 8672 5288</p>
              </CardContent>
            </Card>

            <Card className="p-6 text-center">
              <CardContent className="space-y-4 p-0">
                <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold">Email</h3>
                <p className="text-lg text-gray-600">admin@umoonaopalmine.com.au</p>
              </CardContent>
            </Card>

            <Card className="p-6 text-center">
              <CardContent className="space-y-4 p-0">
                <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                  <MapPin className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold">Address</h3>
                <p className="text-gray-600">14 Hutchison St, Coober Pedy SA 5723, Australia</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Location & Directions */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Find Us in Coober Pedy
            </h2>
            <p className="text-xl text-gray-600">
              We're located on Hutchison Street in the heart of Coober Pedy
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Card className="p-8">
              <CardContent className="space-y-6 p-0">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <Navigation className="w-6 h-6 mr-3 text-blue-600" />
                  Driving Directions
                </h3>
                <p className="text-gray-600 text-lg">
                  Located right on Hutchison Street, the main road through Coober Pedy.
                </p>
                <ul className="space-y-2 text-gray-600">
                  <li>• 846km north of Adelaide</li>
                  <li>• On the Stuart Highway (A87)</li>
                  <li>• Free parking available</li>
                  <li>• Coach parking available</li>
                </ul>
                
                <Alert className="border-blue-200 bg-blue-50">
                  <Car className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <strong>Visitor Tip:</strong> Look for our distinctive signage and the underground entrance. 
                    Free parking is available right outside!
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>

            <Card className="p-8 bg-gradient-to-br from-blue-50 to-teal-50 border-blue-200">
              <CardContent className="space-y-6 p-0">
                <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                  <MapPin className="w-6 h-6 mr-3 text-teal-600" />
                  Visit Information
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Clock className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Hours</p>
                      <p className="text-gray-600">8:30am - 5:30pm Daily</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Phone</p>
                      <p className="text-gray-600">(08) 8672 5288</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <p className="text-gray-600">admin@umoonaopalmine.com.au</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-teal-600 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Address</p>
                      <p className="text-gray-600">14 Hutchison St<br />Coober Pedy SA 5723<br />Australia</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Ready to Visit Umoona?
              </h2>
              <p className="text-xl text-teal-100 max-w-2xl mx-auto">
                Don't wait - start planning your underground adventure today! Contact us for bookings, group rates, or any questions.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild
                size="lg"
                className="bg-white text-teal-600 hover:bg-teal-50 font-semibold px-8 py-4 text-lg"
              >
                <a href="tel:(08) 8672 5288">
                  Call Now: (08) 8672 5288
                  <Phone className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-teal-600 font-semibold px-8 py-4 text-lg"
              >
                <Link to="/underground-tours">
                  Book Underground Tour
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;